import { useEffect, useState } from "react";

/**
 * React hook to check if an element is hidden via CSS. This only works if the element itself
 * is the one that is hidden, not its parent or an ancestor.
 *
 * See {@link isElementVisible} for a list of CSS properties that are checked.
 *
 * @returns {boolean} `true` if the element is visible, `false` if not, or `null` if the
 * element is not yet mounted.
 */
export function useIsVisible(element: HTMLElement): boolean | null {
  const [isVisible, setIsVisible] = useState<boolean | null>(null);

  useEffect(() => {
    if (!element) return;

    return watchElementVisiblity(element, setIsVisible);
  }, [element]);

  return isVisible;
}

/**
 * Watch an element for changes in its visibility. This only works if the element itself
 * is the one that is hidden, not its parent or an ancestor.
 *
 * See {@link isElementVisible} for a list of CSS properties that are checked.
 *
 * @param element The element to watch.
 * @param onVisiblityChanged A callback that will be called when the element's visibility changes.
 * @returns A function that will stop watching the element.
 */
export function watchElementVisiblity(
  element: HTMLElement,
  onVisiblityChanged: (visible: boolean) => void,
): () => void {
  let isVisible: null | boolean = null;

  // Use a MutationObserver to detect changes in the element's style.
  const observer = new MutationObserver((_, __) => {
    const newIsVisible = isElementVisible(element);

    if (isVisible === newIsVisible) return;

    isVisible = newIsVisible;

    onVisiblityChanged(newIsVisible);
  });

  observer.observe(element, { attributes: true });

  return () => observer.disconnect();
}

/**
 * Check if an element is hidden via CSS, such as:
 *
 * - `display: none`
 * - `visibility: hidden`
 * - `opacity: 0`
 * - `overflow: hidden` with `height: 0` or `width: 0`
 *
 * This only works if the element itself is the one that is hidden, not its parent or an
 * ancestor.
 */
export function isElementVisible(element: HTMLElement) {
  const style = getComputedStyle(element);

  if (style.display === "none") return false;

  if (style.visibility === "hidden") return false;

  if (style.opacity === "0") return false;

  if (
    style.overflow === "hidden" &&
    (style.height === "0" || style.width === "0")
  )
    return false;

  return true;
}
